<template>
  <div class="search-helper-comp">
    <div class="search-helper-comp__box">

      <section class="search-helper-comp__title">
        <p>{{$t('searchHelper["Search Helper"]')}}</p>
      </section>

      <section class="search-helper-comp__content">
        <!-- 头部tab -->
        <div :class="{'search-helper-comp__nav--fixed': isFixed}">
          <SnapSearchTabV2
            class="search-helper-comp__nav"
            :search-tabs="searchTabs"
            :current-mode="currentMode"
            :special-tabs="specialTabs"
            :go-special-link="shiftTab"
          />
        </div>
        <div
          v-show="isFixed"
          :class="{'search-helper-comp__fixedSpace': isFixed}"
        ></div>

        <div class="search-helper-comp__container">
          <!-- 左侧导航 -->
          <div
            class="search-helper-comp__left"
            :class="[isFixed?'search-helper-comp__left--fixed':'search-helper-comp__left--noFixed']"
          >
            <LeftNav
              v-model="currentNav"
              :current-nav="currentNav"
              :nav-list="navList"
              @shift-nav-handler="shiftNavHandler"
            />
          </div>

          <!-- 表格视图 -->
          <div class="search-helper-comp__table">
            <FieldView
              v-if="currentMode === definedType.searchableFields"
              :current-nav="currentNav"
              @update-nav-handler="updateNavHandler"
            />
            <OperatorView
              v-if="currentMode === definedType.searchSyntax"
              :current-nav="currentNav"
              @update-nav-handler="updateNavHandler"
            />
            <FormatView
              v-if="currentMode ===  definedType.numberFormat"
              @update-nav-handler="updateNavHandler"
            />
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<script setup lang="ts">
import SnapSearchTabV2 from 'library-@/components/snap-business/search-tab-v2';
import _ from 'lodash';
import $ from 'jquery';

import LeftNav from './components/LeftNav.vue';
import FieldView from './components/FieldView.vue';
import OperatorView from './components/OperatorView.vue';
import FormatView from './components/FormatView.vue';
import { useI18n } from '@/composables/useI18n';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';

const { map, get, forEach, debounce } = _;

const { t } = useI18n();

const definedType = {
    searchableFields: 'searchableFields',
    searchSyntax: 'searchSyntax',
    numberFormat: 'numberFormat',
};

const searchTabs = [
    {
        'key': definedType.searchableFields,
        '$i18Text': t('searchHelper["Searchable Fields"]')
    },
    {
        'key': definedType.searchSyntax,
        '$i18Text': t('searchHelper["Search Syntax"]')
    },
    {
        'key': definedType.numberFormat,
        '$i18Text': t('searchHelper["Number Format"]')
    }
];

const specialTabs = map(searchTabs, item => item.key);

const currentMode = ref(definedType.searchableFields);

const isFixed = ref(false);

const currentNav = ref('');

const navList = ref<{ nav: string }[]>([]);

onMounted(() => {
    bindEvent();
});

onUnmounted(() => {
    offEvent();
});

function backToTop() {
    nextTick(() => window.scrollTo(0, 0));
}

function bindEvent() {
    $(window).on('scroll.SearchHelper', () => {
        if ($(window).scrollTop() > 175) {
            if (!isFixed.value) {
                isFixed.value = true;
                nextTick(() => {
                    const $width = $('.search-helper-comp__table');
                    const $height = $('.search-helper-comp__nav');
                    $('.search-helper-comp__nav').outerWidth(`${$width.outerWidth()}px`);
                    $('.search-helper-comp__fixedSpace').outerHeight(`${$height.outerHeight()}px`);
                });
            }
        } else {
            if (isFixed.value) {
                isFixed.value = false;
                nextTick(() => {
                    $('.search-helper-comp__nav').outerWidth('100%');
                });
            }
        }
        nextTick(() => debounceScrollingUpdateStatus());
    });
}

function offEvent() {
    $(window).off('scroll.SearchHelper');
}

function shiftTab(nav) {
    currentMode.value = nav.key;
}

function updateNavHandler(data) {
    navList.value = data;
    currentNav.value = get(data, '[0].nav', '');
    isFixed.value = false;
    backToTop();
}

function shiftNavHandler(newNav) {
    nextTick(() => {
        const baseHeight = $(window).scrollTop() || 0;
        const targetOffsetHeight = $(newNav)[0] && $(newNav)[0].getBoundingClientRect ? $(newNav)[0].getBoundingClientRect().top : 0;
        window.scrollTo(0, baseHeight + targetOffsetHeight - 100);
    });
}

function scrollingUpdateStatus() {
    const isInView = pos => {
        return pos >= 0 && pos <= document.body.clientHeight;
    };
    const isToBottom = () => {
        const $window = $(window);
        const scrollTop = $window.scrollTop();
        const windowHeight = $window.height();
        const scrollHeight = $(document).height();
        return scrollTop + windowHeight === scrollHeight;
    };
    const visible: Record<'body'|'top'|'bottom', { nav: string }[]> = {
        body: [],
        top: [],
        bottom: []
    };
    forEach(navList.value, (iNav, originIndex) => {
        const { nav } = iNav;
        const { top, bottom } = $(iNav.nav).parent()[0].getBoundingClientRect();
        let visibleArea = '';
        if (top < 0 && bottom > document.body.clientHeight) {
            visibleArea = 'body';
        } else if (isInView(top)) {
            visibleArea = 'top';
        } else if (isInView(bottom)) {
            visibleArea = 'bottom';
        }
        if (visibleArea) {
            visible[visibleArea].push({
                nav,
                visibleArea: visibleArea,
                originIndex
            });
        }
    });

    // 当前锚点判断优先级： body > top(滚动条底部的时候，current为最后一个top) > bottom
    const updateFn = pos => {
        currentNav.value = visible[pos][0].nav;
    };
    if (visible.body.length) {
        updateFn('body');
    } else if (visible.top.length) {
        if (isToBottom()) {
            const last = visible.top.length - 1;
            currentNav.value = visible.top[last].nav;
        } else {
            updateFn('top');
        }
    } else if (visible.bottom.length) {
        updateFn('bottom');
    }
}

const debounceScrollingUpdateStatus = debounce(scrollingUpdateStatus, 100);
</script>

<style lang="scss">
@import './src/commTable.scss';

.search-helper-comp {
  * {
    box-sizing: border-box;
  }
  text-align: center;
  padding-bottom: 88px;
  background: rgba(250, 251, 252, 1);
  min-height: calc(100% - 64px);
  &__box {
    width: 960px;
    display: inline-block;
    text-align: left;
  }
  &__title {
    padding-top: 48px;
    padding-bottom: 32px;
    p {
      height: 40px;
      line-height: 40px;
      font-size: 32px;
      font-weight: 400;
      color: rgba(97, 97, 97, 1);
    }
  }
  &__nav.search-tabs-v2 {
    width: 100%;
  }
  &__container {
    padding-top: 40px;
    position: relative;
  }
  &__left {
    &--noFixed {
      position: absolute;
      left: -146px;
      top: 40px;
    }
    &--fixed {
      position: fixed;
      top: 74px;
      margin-left: -146px;
    }
  }
  &__nav--fixed {
    position: fixed;
    top: 4px;
    z-index: 10;
    .search-helper-comp__nav {
      background: #fafbfc;
      position: relative;
      &:before,
      &:after {
        content: '';
        width: 100%;
        position: absolute;
        display: block;
        left: 0;
        background: #fafbfc;
      }
      &:before {
        height: 4px;
        top: -4px;
      }
      &:after {
        height: 12px;
        bottom: -13px;
      }
    }
  }
}
</style>
